import { IAlertProps } from "@components/alert/Alert.types";
import { TId } from "@components/table";
import { withTheme } from "@hoc/withTheme";
import { InboxFileEntity } from "@odata/GeneratedEntityTypes";
import { InboxEntityTypeCode } from "@odata/GeneratedEnums";
import { transformToODataString } from "@odata/OData.utils";
import { WithOData, withOData } from "@odata/withOData";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import Dialog from "../../components/dialog";
import FileStrip from "../../components/fileStrip";
import { AppContext, IAppContext } from "../../contexts/appContext/AppContext.types";
import { ValueType } from "../../enums";
import { PropsWithTheme } from "../../theme";
import memoize from "../../utils/memoize";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";
import { ISplitPagePaneData } from "../Page";
import { IDefinition } from "../PageUtils";
import { OtherFolderWindowStyled } from "./Inbox.styles";
import {
    getInboxFolderFileStripeItem,
    getInboxTableDef,
    getPaneData,
    handleAttachFiles,
    InboxFilter
} from "./Inbox.utils";
import { getDefinitions } from "./InboxDef";
import InboxSplitPage from "./InboxSplitPage";


interface IProps extends WithOData, PropsWithTheme, WithTranslation {
    onAttachFiles?: (inboxFiles: TId[]) => Promise<boolean>;
    onClose?: () => void;
    // Inbox files to hide - they are temporarily attached to new entity without draft,
    // so they behave like they are already deleted from inbox, but actually the conversion is done on entity save
    hideInboxFileIds?: number[];
}

class OtherFolderWindow extends React.PureComponent<IProps> {
    static contextType = AppContext;

    definition: IDefinition;

    componentDidMount() {
        if (this.props.tReady) {
            this.init();
        }
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<{}>, snapshot?: any) {
        if (!prevProps.tReady && this.props.tReady) {
            this.init();
        }
    }

    init(): void {
        const context = this.context as IAppContext;
        const type = InboxFilter.Other;
        const definition = getDefinitions(context);
        const tableDef = getInboxTableDef(definition, type);
        const _origFilter = tableDef.filter as string;
        definition.table = {
            ...tableDef,
            preventStoreVariant: true,
            id: "otherFolderSpecialTable",
            filter: () => {
                const { hideInboxFileIds } = this.props;
                return hideInboxFileIds?.length ? `not(${InboxFileEntity.Id} in (${transformToODataString(hideInboxFileIds, ValueType.Number)})) AND ${_origFilter}`
                        : _origFilter;
            }
        };
        this.definition = definition;
        this.forceUpdate();
    }

    getPaneData = memoize((type: InboxFilter): ISplitPagePaneData<ISplitPageTableDef> =>
            getPaneData(this.definition, type));

    handleAttachFiles = (Ids: TId[]): Promise<IAlertProps> => {
        return handleAttachFiles("Inbox:TableView.CompleteSuccessOther", () => this.props.onAttachFiles?.(Ids));
    };

    handleClose = () => {
        this.props.onClose?.();
    };

    renderContent() {
        const otherFolder = getInboxFolderFileStripeItem(InboxEntityTypeCode.Other);
        const paneData = this.getPaneData(InboxFilter.Other);

        return (
                <OtherFolderWindowStyled>
                    <FileStrip items={[otherFolder]}
                               selectedId={otherFolder.id}/>

                    <InboxSplitPage t={this.props.t}
                                    onAttachFiles={this.handleAttachFiles}
                                    inEditableWindow
                                    selectedFolderId={otherFolder.id}
                                    supportedEntities={[InboxEntityTypeCode.Other]}
                                    paneData={paneData}
                                    hideInboxFileIds={this.props.hideInboxFileIds}
                    />
                </OtherFolderWindowStyled>
        );
    }

    render() {
        const isBusy = !this.definition;

        return (
                <Dialog
                        disableScroll
                        width="100%"
                        height="100%"
                        isEditableWindow
                        busy={isBusy}
                        onConfirm={null}
                        onClose={this.handleClose}>
                    {!isBusy && this.renderContent()}
                </Dialog>
        );
    }
}

export default withTheme(withOData(withTranslation("Inbox")(OtherFolderWindow)));